/**
 * LiveMeetingNow — TASK-667.
 *
 * A compact, PASSIVE tile for bot-attended meetings. When Attendee covers a meeting the RM
 * is inside Teams and Kielis has no surface at all — the pipeline runs headless and
 * everything it learns waits until after the call. This is the glanceable answer: which
 * meeting is live, that the bot is capturing, and the grounded transcript tail.
 *
 * It is a MONITOR, not a control surface. The bot owns the audio, so there is nothing to
 * operate — and critically, **this tile never starts a microphone**. It drives
 * `useMeetingCapture` in `subscribeOnly` mode, which opens the socket without sending
 * `session_start` and without touching `getUserMedia`.
 *
 * Honesty rules it encodes (TASK-662 / TASK-427):
 *   - **Live analysis does not exist.** During the call it shows only source-grounded bot
 *     status, timing and utterances. Never an insight, proposal or detected-item count.
 *   - After the call, counts appear only when their batch results are terminal. Until then
 *     it says `Analysis pending`; a partial failure is named and offers recovery.
 *   - One tile = one session. Concurrent live sessions are distinct, addressable tiles.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Radio, X } from 'lucide-react';

import { useMeetingCapture } from '../../../hooks/useMeetingCapture';
import { httpClient } from '../../../lib/httpClient';
import { useAccount } from '../../../contexts/AccountContext';
import { useCanvasActions } from '../../../stores/canvasStore';
import { WidgetContainer } from '../WidgetContainer';

/** How many utterances the tail keeps. Short enough to sit beside a Teams window. */
const TAIL_SIZE = 4;

interface LiveMeetingNowProps {
  sessionId?: string;
  clientName?: string;
  meetingType?: string;
  onDismiss?: () => void;
}

interface AnalysisStatus {
  state: 'not_started' | 'processing' | 'completed' | 'partial_failure' | 'failed';
  message: string;
  retryable: boolean;
  skills: Record<string, string>;
  /** TASK-664 review: post-skill steps that errored (`kyc_staging`, `diarization_stitch`).
   *  They are already named in `message`, which the partial_failure branch renders — so a
   *  failed staging step is visible rather than hidden behind "Analysis complete". */
  failed_steps?: string[];
}

const BOT_STATUS_COPY: Record<string, { label: string; tone: string }> = {
  joining: { label: 'Bot is joining the meeting', tone: 'text-blue-700 bg-blue-50 border-blue-200' },
  'in-call': { label: 'Bot is capturing', tone: 'text-green-700 bg-green-50 border-green-200' },
  left: { label: 'Bot has left the meeting', tone: 'text-gray-700 bg-gray-50 border-gray-200' },
  failed: { label: 'Bot failed to join', tone: 'text-red-700 bg-red-50 border-red-200' },
};

export function LiveMeetingNow({
  sessionId,
  clientName,
  meetingType,
  onDismiss,
}: LiveMeetingNowProps) {
  const { addSpecs } = useCanvasActions();
  const { account, user } = useAccount();

  // TASK-673: "Open meeting" keeps the RM on the canvas — summon the recorder tile rather
  // than routing out to the full /meeting/:id/live page.
  const openRecorderOnCanvas = () =>
    addSpecs([{ component: 'MeetingRecorderWidget', props: { sessionId, clientName } }]);
  const [tail, setTail] = useState<{ seq: number; text: string }[]>([]);
  const [startedAt] = useState(() => Date.now());
  const [elapsed, setElapsed] = useState(0);

  // subscribeOnly: socket for bot_status + chunk_transcribed, never a microphone.
  const capture = useMeetingCapture({
    sessionId,
    accountId: account?.id,
    userId: user?.id,
    userRole: user?.role,
    subscribeOnly: true,
    onTranscript: (chunk) =>
      setTail((prev) => {
        // Idempotent by seq — a re-published chunk replaces rather than duplicates.
        const next = prev.filter((c) => c.seq !== chunk.seq).concat(chunk);
        next.sort((a, b) => a.seq - b.seq);
        return next.slice(-TAIL_SIZE);
      }),
  });

  const { botState, isConnected } = capture;
  // `failed` is NOT `ended`. A bot that never joined captured nothing, so presenting it as
  // a finished meeting with a review action would send the RM to an empty review screen.
  // It gets its own state and its own next action (open the meeting and capture it another
  // way). `left` is the normal end of a call.
  const failed = botState === 'failed';
  const ended = botState === 'left';

  useEffect(() => {
    if (ended || failed) return;
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [ended, startedAt]);

  // The tile must be correct for someone who opens the dashboard mid-meeting, so recent
  // utterances are fetched on mount rather than relying on the stream alone.
  const backfill = useQuery({
    queryKey: ['live-meeting-tail', sessionId],
    queryFn: () =>
      httpClient.get<{ utterances?: { seq: number; text: string }[] }>(
        `/api/meeting/${sessionId}/transcript-excerpt`,
      ),
    enabled: !!sessionId,
    staleTime: 30_000,
    retry: false,
  });

  useEffect(() => {
    const rows = backfill.data?.utterances;
    if (!rows?.length) return;
    setTail((prev) => (prev.length ? prev : rows.slice(-TAIL_SIZE)));
  }, [backfill.data]);

  // Batch state only once the call has ended — during the call there is nothing terminal
  // to report and showing a count would be a lie.
  const analysis = useQuery<AnalysisStatus>({
    queryKey: ['analysis-status', sessionId],
    queryFn: () => httpClient.get<AnalysisStatus>(`/api/meeting/${sessionId}/analysis-status`),
    enabled: !!sessionId && ended,  // not on `failed` — nothing was captured to analyse
    refetchInterval: (q) =>
      q.state.data?.state === 'processing' || q.state.data?.state === 'not_started' ? 10_000 : false,
  });

  const status = useMemo(() => {
    if (botState && BOT_STATUS_COPY[botState]) return BOT_STATUS_COPY[botState];
    // TASK-667 review: this branch used to return the same copy as the fallback below, so
    // the AC's "disconnected stream" state was indistinguishable from "not started yet".
    // They need different words because they need different actions: waiting is normal,
    // a dropped socket means what is on screen has stopped updating.
    if (!isConnected) {
      return {
        label: 'Live connection lost — this view is not updating',
        tone: 'text-amber-700 bg-amber-50 border-amber-200',
      };
    }
    return { label: 'Waiting for the bot to join', tone: 'text-gray-700 bg-gray-50 border-gray-200' };
  }, [botState, isConnected]);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <WidgetContainer title={clientName ? `Live — ${clientName}` : 'Live meeting'}>
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            {meetingType && <p className="text-xs text-muted-foreground">{meetingType}</p>}
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss live meeting tile"
              className="rounded p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Status — icon + words + colour, announced on change. */}
        <div
          role="status"
          aria-live="polite"
          className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${
            failed
              ? 'text-red-700 bg-red-50 border-red-200'
              : ended
                ? 'text-gray-700 bg-gray-50 border-gray-200'
                : status.tone
          }`}
        >
          <span className="flex items-center gap-2">
            {failed ? (
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            ) : ended ? (
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Radio className="h-4 w-4" aria-hidden="true" />
            )}
            {failed ? 'Bot failed to join' : ended ? 'Meeting ended' : status.label}
          </span>
          <span className="font-mono tabular-nums text-xs" aria-label={`Elapsed ${mm}:${ss}`}>
            {mm}:{ss}
          </span>
        </div>

        {failed ? (
          <>
            {/* Nothing was captured, so there is no transcript and no analysis to offer.
                The honest next action is to open the meeting and capture it another way. */}
            <p className="rounded-lg border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
              The bot never joined, so nothing was recorded. Open the meeting to record it
              yourself, or dictate a summary afterwards.
            </p>
            <button
              type="button"
              onClick={openRecorderOnCanvas}
              className="w-full rounded-lg bg-kielis-navy px-3 py-2 text-sm font-medium text-white hover:bg-kielis-navy/90"
            >
              Open meeting
            </button>
          </>
        ) : !ended ? (
          <>
            {/* Grounded tail only — no insight, proposal or detected-item count exists
                during a call, so none is rendered. */}
            <div className="rounded-lg border border-border bg-muted/20 p-2">
              {tail.length === 0 ? (
                <p className="py-2 text-center text-xs text-muted-foreground">
                  {/* An empty tail on a DROPPED socket does not mean "nothing was said" —
                      it means we stopped receiving. Saying "waiting for the bot" there
                      would attribute our own disconnection to the bot. */}
                  {!isConnected
                    ? 'Not receiving the live transcript.'
                    : botState === 'in-call'
                      ? 'No speech yet.'
                      : 'Waiting for the bot to join.'}
                </p>
              ) : (
                <ul className="space-y-1">
                  {tail.map((c) => (
                    <li key={c.seq} className="text-xs leading-snug text-foreground">
                      {c.text}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={openRecorderOnCanvas}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Open meeting
            </button>
          </>
        ) : (
          <>
            <div role="status" aria-live="polite" className="rounded-lg border border-border p-3 text-sm">
              {analysis.isLoading || analysis.data?.state === 'processing' || analysis.data?.state === 'not_started' ? (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Analysis pending
                </p>
              ) : analysis.isError || !analysis.data ? (
                // TASK-667 fix: a FAILED status fetch (data undefined) must NOT be painted
                // green "Analysis complete" — that told the RM a meeting was analysed when
                // the check itself never succeeded. Say so, and offer a retry.
                <p className="flex flex-wrap items-center gap-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>Couldn&apos;t check the analysis status.</span>
                  <button
                    type="button"
                    onClick={() => analysis.refetch()}
                    className="font-medium underline hover:no-underline"
                  >
                    Retry
                  </button>
                </p>
              ) : analysis.data.state === 'partial_failure' || analysis.data.state === 'failed' ? (
                <p className="flex items-start gap-2 text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{analysis.data.message}</span>
                </p>
              ) : analysis.data.state === 'completed' ? (
                <p className="flex items-center gap-2 text-green-800">
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  Analysis complete
                </p>
              ) : (
                // Any unrecognised state is reported literally, never as success.
                <p className="flex items-center gap-2 text-muted-foreground">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  Analysis status: {analysis.data.state}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() =>
                addSpecs([
                  { component: 'PostMeetingReviewWidget', props: { sessionId, clientName } },
                ])
              }
              className="w-full rounded-lg bg-kielis-navy px-3 py-2 text-sm font-medium text-white hover:bg-kielis-navy/90"
            >
              Review &amp; document
            </button>
          </>
        )}
      </div>
    </WidgetContainer>
  );
}

export default LiveMeetingNow;
